import type { DateRange } from '@room-mates/shared';
import { type ReactElement, useState } from 'react';
import { formatRange, nightsInRange } from '../../shared/format.js';
import { useTranslation } from '../../shared/i18n/useLanguage.js';
import { Button } from '../../shared/ui/index.js';
import { DateRangeCalendar, type PartialDateRange } from './DateRangeCalendar.js';

export interface DateRangeEditorProps {
  readonly ranges: readonly DateRange[];
  /** The sojourn window; every range must sit inside it. */
  readonly bounds: DateRange;
  readonly disabled?: boolean;
  onChange(ranges: DateRange[]): void;
}

type EditorMode =
  | { readonly kind: 'idle' }
  | { readonly kind: 'adding' }
  | { readonly kind: 'editing'; readonly index: number };

const IDLE: EditorMode = { kind: 'idle' };
const EMPTY_DRAFT: PartialDateRange = { start: null, end: null };

/**
 * Edits the list of periods somebody is present, or a house is usable.
 *
 * Several periods is the normal case, not an edge case: guests leave and come
 * back, houses free up again. Each one is picked from a calendar — arrival,
 * then departure, two clicks — instead of typed into a pair of native date
 * fields one number segment at a time, which was both slower and where most
 * invalid ranges came from.
 */
export function DateRangeEditor({
  ranges,
  bounds,
  disabled = false,
  onChange,
}: DateRangeEditorProps): ReactElement {
  const t = useTranslation();
  const [mode, setMode] = useState<EditorMode>(IDLE);
  const [draft, setDraft] = useState<PartialDateRange>(EMPTY_DRAFT);

  const startAdding = (): void => {
    setDraft(EMPTY_DRAFT);
    setMode({ kind: 'adding' });
  };

  const startEditing = (index: number): void => {
    const range = ranges[index];
    if (range === undefined) {
      return;
    }
    setDraft({ start: range.start, end: range.end });
    setMode({ kind: 'editing', index });
  };

  const cancel = (): void => setMode(IDLE);

  const commit = (): void => {
    if (draft.start === null || draft.end === null) {
      return;
    }
    const range: DateRange = { start: draft.start, end: draft.end };

    if (mode.kind === 'adding') {
      onChange([...ranges, range]);
    } else if (mode.kind === 'editing') {
      onChange(ranges.map((existing, index) => (index === mode.index ? range : existing)));
    }
    setMode(IDLE);
  };

  const canCommit = draft.start !== null && draft.end !== null;
  const otherRowsDisabled = disabled || mode.kind !== 'idle';

  return (
    <div className="stack stack--tight">
      <ul className="data-list">
        {ranges.map((range, index) =>
          mode.kind === 'editing' && mode.index === index ? (
            <li key={`${range.start}-${range.end}-${index}`}>
              <div className="panel stack stack--tight">
                <DateRangeCalendar
                  bounds={bounds}
                  value={draft}
                  disabled={disabled}
                  onChange={setDraft}
                />
                <div className="row">
                  <Button small onClick={commit} disabled={disabled || !canCommit}>
                    {t.common.saveChanges}
                  </Button>
                  <Button small variant="ghost" onClick={cancel} disabled={disabled}>
                    {t.common.cancel}
                  </Button>
                </div>
              </div>
            </li>
          ) : (
            <li key={`${range.start}-${range.end}-${index}`} className="list-row">
              <span className="list-row__main">
                <span className="list-row__title">{formatRange(range)}</span>
                <span className="list-row__meta">
                  {t.count.nights(Math.max(0, nightsInRange(range)))}
                </span>
              </span>
              <div className="row">
                <Button
                  small
                  variant="secondary"
                  disabled={otherRowsDisabled}
                  onClick={() => startEditing(index)}
                >
                  {t.common.edit}
                </Button>
                <Button
                  small
                  variant="danger"
                  disabled={otherRowsDisabled || ranges.length === 1}
                  onClick={() => onChange(ranges.filter((_, i) => i !== index))}
                >
                  {t.common.remove}
                </Button>
              </div>
            </li>
          ),
        )}
      </ul>

      {mode.kind === 'adding' ? (
        <div className="panel stack stack--tight">
          <DateRangeCalendar
            bounds={bounds}
            value={draft}
            disabled={disabled}
            onChange={setDraft}
          />
          <div className="row">
            <Button small onClick={commit} disabled={disabled || !canCommit}>
              {t.dateRangeEditor.addPeriod}
            </Button>
            <Button small variant="ghost" onClick={cancel} disabled={disabled}>
              {t.common.cancel}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          small
          variant="secondary"
          disabled={disabled || mode.kind === 'editing'}
          onClick={startAdding}
        >
          {t.dateRangeEditor.addAPeriod}
        </Button>
      )}
    </div>
  );
}
